/**
 * Right rail beside the canvas: Objects, History, spacer, Dashboard.
 * State lives in Photorealistic; this component is presentational + wiring callbacks.
 */
const disabledRailButtonClass =
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none disabled:hover:bg-white disabled:hover:text-gray-700';

export default function PhotorealisticCanvasToolAside({
  swapped,
  image,
  showPolygonsOverlay,
  showHistoryPanel,
  showModelStudioPanel,
  showPromptPanel,
  showRenderPanel,
  showFlythroughPanel,
  imageSelectionActive = false,
  onToggleObjectsClick,
  onToggleHistoryClick,
  onToggleModelStudioClick,
  onTogglePromptClick,
  onToggleRenderClick,
  onToggleFlythroughClick,
  onBackToDashboard,
}) {
  return (
    <aside className="w-[6rem] shrink-0 bg-white py-6 flex flex-col gap-2 min-h-0">
      {!swapped && (
        <button
          type="button"
          onClick={onToggleObjectsClick}
          className={`w-full flex flex-col items-center justify-center gap-2 text-[10px] font-medium px-1 py-2 transition-colors ${
            showPolygonsOverlay
              ? 'bg-primary-300 text-white border-gray-800 '
              : 'bg-white text-gray-700 border-gray-200 hover:bg-primary-200 hover:text-white'
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="20"
            viewBox="0 0 22 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path
              d="M1.29959 11.156C1.02759 10.783 0.891595 10.596 0.797595 10.236C0.734135 9.91544 0.734135 9.58556 0.797595 9.265C0.891595 8.904 1.02759 8.717 1.29959 8.345C2.78859 6.3 6.05259 2.75 10.7496 2.75C15.4466 2.75 18.7106 6.3 20.1996 8.344C20.4716 8.717 20.6076 8.904 20.7016 9.264C20.7651 9.58456 20.7651 9.91444 20.7016 10.235C20.6076 10.596 20.4716 10.783 20.1996 11.155C18.7106 13.2 15.4466 16.75 10.7496 16.75C6.05259 16.75 2.78859 13.2 1.29959 11.156Z"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M19.75 0.75L1.75 18.75M10.75 11.75C11.2804 11.75 11.7891 11.5393 12.1642 11.1642C12.5393 10.7891 12.75 10.2804 12.75 9.75C12.75 9.21957 12.5393 8.71086 12.1642 8.33579C11.7891 7.96071 11.2804 7.75 10.75 7.75C10.2196 7.75 9.71086 7.96071 9.33579 8.33579C8.96071 8.71086 8.75 9.21957 8.75 9.75C8.75 10.2804 8.96071 10.7891 9.33579 11.1642C9.71086 11.5393 10.2196 11.75 10.75 11.75Z"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {showPolygonsOverlay ? 'Objects' : 'Objects'}
        </button>
      )}

      {!swapped && image && (
        <>
          <button
            type="button"
            disabled={imageSelectionActive}
            onClick={onToggleHistoryClick}
            className={`w-full flex flex-col items-center justify-center gap-2 text-[10px] font-medium px-1 py-2 transition-colors ${disabledRailButtonClass} ${
              showHistoryPanel
                ? 'bg-primary-300 text-white border-gray-300'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-primary-200 hover:text-white'
            }`}
            title={
              imageSelectionActive
                ? 'Unavailable while Image Selection is active'
                : 'Version control'
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path
                d="M3.386 16.114C4.43256 17.1605 5.72107 17.9328 7.1374 18.3625C8.55372 18.7921 10.0541 18.8658 11.5058 18.5771C12.9574 18.2883 14.3154 17.646 15.4595 16.7071C16.6036 15.7682 17.4984 14.5616 18.0648 13.1942C18.6312 11.8268 18.8517 10.3408 18.7066 8.86787C18.5615 7.39495 18.0554 5.98053 17.2332 4.74991C16.4109 3.51928 15.2978 2.51044 13.9925 1.81274C12.6873 1.11504 11.2301 0.750013 9.75 0.75C5.692 0.75 3.232 3.455 0.75 6.25"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M0.75 2.25V6.25H4.75"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            History
          </button>
        </>
      )}

      {!swapped && (
        <>
          <button
            type="button"
            disabled={imageSelectionActive}
            onClick={onToggleModelStudioClick}
            className={`w-full flex flex-col items-center justify-center gap-2 text-[10px] font-medium px-1 py-2 transition-colors ${disabledRailButtonClass} ${
              showModelStudioPanel
                ? 'bg-primary-300 text-white border-gray-300'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-primary-200 hover:text-white'
            }`}
            title={
              imageSelectionActive
                ? 'Unavailable while Image Selection is active'
                : 'Model Studio'
            }
          >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5.75 7.75L9.75 9.75M5.75 7.75V11.75L9.75 13.75M5.75 7.75L9.75 5.75L13.75 7.75M9.75 9.75L13.75 7.75M9.75 9.75V13.75M9.75 13.75L13.75 11.75V7.75M7.15 18.75C4.91 18.75 3.79 18.75 2.934 18.314C2.18139 17.9305 1.56949 17.3186 1.186 16.566C0.75 15.71 0.75 14.59 0.75 12.35M18.75 12.35C18.75 14.59 18.75 15.71 18.314 16.566C17.9305 17.3186 17.3186 17.9305 16.566 18.314C15.71 18.75 14.59 18.75 12.35 18.75M12.35 0.75C14.59 0.75 15.71 0.75 16.566 1.186C17.3186 1.56949 17.9305 2.18139 18.314 2.934C18.75 3.79 18.75 4.91 18.75 7.15M7.15 0.75C4.91 0.75 3.79 0.75 2.934 1.186C2.18139 1.56949 1.56949 2.18139 1.186 2.934C0.75 3.79 0.75 4.91 0.75 7.15" />
              </svg>
            Model Studio
          </button>

          <button
            type="button"
            disabled={imageSelectionActive}
            onClick={onTogglePromptClick}
            className={`w-full flex flex-col items-center justify-center gap-2 text-[10px] font-medium px-1 py-2 transition-colors ${disabledRailButtonClass} ${
              showPromptPanel
                ? 'bg-primary-300 text-white border-gray-300'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-primary-200 hover:text-white'
            }`}
            title={
              imageSelectionActive
                ? 'Unavailable while Image Selection is active'
                : 'Prompt'
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M21.059 11.003C21.1899 11.0134 21.3175 11.0494 21.4345 11.1091C21.5515 11.1688 21.6556 11.2509 21.7409 11.3508C21.8261 11.4508 21.8909 11.5665 21.9314 11.6914C21.9719 11.8164 21.9874 11.9481 21.977 12.079C21.618 16.65 17.138 20 12 20C11.348 20 10.711 19.944 10.093 19.843L9.979 19.932C9.86832 20.0214 9.75493 20.1075 9.639 20.19C9.071 20.59 8.249 21 7 21C6.73478 21 6.48043 20.8946 6.29289 20.7071C6.10536 20.5196 6 20.2652 6 20C6 19.611 6.046 19.258 6.009 18.771C6.00067 18.6068 5.97212 18.4442 5.924 18.287C5.91533 18.2657 5.908 18.2497 5.902 18.239C3.576 16.718 2 14.296 2 11.5C2 6.643 6.656 3 12 3C12.3687 3 12.7327 3.01667 13.092 3.05C13.3562 3.0744 13.5998 3.20274 13.7693 3.40678C13.9389 3.61082 14.0204 3.87384 13.996 4.138C13.9716 4.40216 13.8433 4.6458 13.6392 4.81533C13.4352 4.98486 13.1722 5.0664 12.908 5.042C12.6061 5.01424 12.3031 5.00023 12 5C7.403 5 4 8.073 4 11.5C4 13.514 5.142 15.372 7.042 16.597C7.469 16.872 7.69 17.284 7.809 17.617C7.929 17.957 7.979 18.315 8.003 18.62C8.007 18.684 8.00967 18.7497 8.011 18.817C8.201 18.739 8.355 18.647 8.486 18.554C8.57164 18.4933 8.65535 18.43 8.737 18.364C8.814 18.304 8.919 18.218 9.017 18.148C9.157 18.048 9.554 17.761 10.079 17.815L10.185 17.832L10.627 17.904C11.0723 17.968 11.53 18 12 18C16.402 18 19.727 15.169 19.983 11.92C20.0042 11.6557 20.1294 11.4107 20.3312 11.2388C20.5329 11.0668 20.7947 10.982 21.059 11.003ZM20 1C20.2086 1.00008 20.4119 1.06539 20.5816 1.18679C20.7512 1.30819 20.8786 1.47959 20.946 1.677L21.076 2.055C21.376 2.934 22.066 3.625 22.946 3.925L23.323 4.054C23.5202 4.12157 23.6913 4.24908 23.8125 4.41869C23.9337 4.58831 23.9988 4.79155 23.9988 5C23.9988 5.20845 23.9337 5.41169 23.8125 5.58131C23.6913 5.75092 23.5202 5.87843 23.323 5.946L22.945 6.076C22.066 6.376 21.375 7.066 21.075 7.946L20.946 8.323C20.8784 8.5202 20.7509 8.69134 20.5813 8.81252C20.4117 8.93369 20.2085 8.99883 20 8.99883C19.7915 8.99883 19.5883 8.93369 19.4187 8.81252C19.2491 8.69134 19.1216 8.5202 19.054 8.323L18.924 7.945C18.7759 7.51114 18.5303 7.11699 18.2062 6.79283C17.882 6.46866 17.4879 6.2231 17.054 6.075L16.677 5.946C16.4798 5.87843 16.3087 5.75092 16.1875 5.58131C16.0663 5.41169 16.0012 5.20845 16.0012 5C16.0012 4.79155 16.0663 4.58831 16.1875 4.41869C16.3087 4.24908 16.4798 4.12157 16.677 4.054L17.055 3.924C17.934 3.624 18.625 2.934 18.925 2.054L19.054 1.677L19.116 1.531C19.2012 1.37054 19.3285 1.23632 19.4843 1.14276C19.64 1.04921 19.8183 0.999855 20 1ZM20 4.196C19.7634 4.4937 19.4937 4.76344 19.196 5C19.4953 5.23667 19.7633 5.50433 20 5.803C20.2373 5.505 20.505 5.23733 20.803 5C20.5056 4.76339 20.2362 4.49364 20 4.196Z"
                fill="currentColor"
              />
            </svg>
            Prompt
          </button>

          <button
            type="button"
            disabled={imageSelectionActive}
            onClick={onToggleRenderClick}
            className={`w-full flex flex-col items-center justify-center gap-2 text-[10px] font-medium px-1 py-2 transition-colors ${disabledRailButtonClass} ${
              showRenderPanel
                ? 'bg-primary-300 text-white border-gray-300'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-primary-200 hover:text-white'
            }`}
            title={
              imageSelectionActive
                ? 'Unavailable while Image Selection is active'
                : 'Render'
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M9.367 2.25H9.4C9.59891 2.25 9.78968 2.32902 9.93033 2.46967C10.071 2.61032 10.15 2.80109 10.15 3C10.15 3.19891 10.071 3.38968 9.93033 3.53033C9.78968 3.67098 9.59891 3.75 9.4 3.75C8.268 3.75 7.463 3.75 6.834 3.802C6.214 3.852 5.829 3.949 5.524 4.104C4.91263 4.41556 4.41556 4.91263 4.104 5.524C3.949 5.829 3.853 6.214 3.802 6.834C3.751 7.464 3.75 8.268 3.75 9.4V14.6C3.75 15.733 3.75 16.537 3.802 17.166C3.852 17.786 3.949 18.171 4.104 18.476C4.41556 19.0874 4.91263 19.5844 5.524 19.896C5.829 20.051 6.214 20.147 6.834 20.198C7.464 20.249 8.268 20.25 9.4 20.25H14.6C15.733 20.25 16.537 20.25 17.166 20.198C17.786 20.148 18.171 20.051 18.476 19.896C19.0874 19.5844 19.5844 19.0874 19.896 18.476C20.051 18.171 20.147 17.786 20.198 17.166C20.249 16.536 20.25 15.732 20.25 14.6V13.5C20.25 13.3011 20.329 13.1103 20.4697 12.9697C20.6103 12.829 20.8011 12.75 21 12.75C21.1989 12.75 21.3897 12.829 21.5303 12.9697C21.671 13.1103 21.75 13.3011 21.75 13.5V14.633C21.75 15.725 21.75 16.591 21.693 17.288C21.635 18.002 21.513 18.605 21.233 19.157C20.7774 20.0505 20.0507 20.7768 19.157 21.232C18.605 21.513 18.002 21.635 17.288 21.693C16.591 21.75 15.725 21.75 14.633 21.75H9.367C8.275 21.75 7.409 21.75 6.712 21.693C5.998 21.635 5.395 21.513 4.844 21.233C3.95014 20.7776 3.22341 20.0509 2.768 19.157C2.487 18.605 2.365 18.002 2.307 17.288C2.25 16.591 2.25 15.725 2.25 14.633V9.367C2.25 8.275 2.25 7.409 2.307 6.712C2.365 5.998 2.487 5.395 2.767 4.844C3.22267 3.94999 3.94977 3.22324 4.844 2.768C5.394 2.487 5.998 2.365 6.712 2.307C7.409 2.25 8.275 2.25 9.367 2.25ZM13.5 3C13.5 2.80109 13.579 2.61032 13.7197 2.46967C13.8603 2.32902 14.0511 2.25 14.25 2.25H21C21.1989 2.25 21.3897 2.32902 21.5303 2.46967C21.671 2.61032 21.75 2.80109 21.75 3V9.75C21.75 9.94891 21.671 10.1397 21.5303 10.2803C21.3897 10.421 21.1989 10.5 21 10.5C20.8011 10.5 20.6103 10.421 20.4697 10.2803C20.329 10.1397 20.25 9.94891 20.25 9.75V4.81L13.28 11.78C13.2113 11.8537 13.1285 11.9128 13.0365 11.9538C12.9445 11.9948 12.8452 12.0168 12.7445 12.0186C12.6438 12.0204 12.5438 12.0018 12.4504 11.9641C12.357 11.9264 12.2722 11.8703 12.201 11.799C12.1297 11.7278 12.0736 11.643 12.0359 11.5496C11.9982 11.4562 11.9796 11.3562 11.9814 11.2555C11.9832 11.1548 12.0052 11.0555 12.0462 10.9635C12.0872 10.8715 12.1463 10.7887 12.22 10.72L19.19 3.75H14.25C14.0511 3.75 13.8603 3.67098 13.7197 3.53033C13.579 3.38968 13.5 3.19891 13.5 3Z"
                fill="currentColor"
              />
            </svg>
            Render
          </button>

          <button
            type="button"
            disabled={imageSelectionActive}
            onClick={onToggleFlythroughClick}
            className={`w-full flex flex-col items-center justify-center gap-2 text-[10px] font-medium px-1 py-2 transition-colors ${disabledRailButtonClass} ${
              showFlythroughPanel
                ? 'bg-primary-300 text-white border-gray-300'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-primary-200 hover:text-white'
            }`}
            title={
              imageSelectionActive
                ? 'Unavailable while Image Selection is active'
                : 'Flythrough'
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M3 9.4C3 7.16 3 6.04 3.436 5.184C3.81949 4.43139 4.43139 3.81949 5.184 3.436C6.04 3 7.16 3 9.4 3H14.6C16.84 3 17.96 3 18.816 3.436C19.5686 3.81949 20.1805 4.43139 20.564 5.184C21 6.04 21 7.16 21 9.4V14.6C21 16.84 21 17.96 20.564 18.816C20.1805 19.5686 19.5686 20.1805 18.816 20.564C17.96 21 16.84 21 14.6 21H9.4C7.16 21 6.04 21 5.184 20.564C4.43139 20.1805 3.81949 19.5686 3.436 18.816C3 17.96 3 16.84 3 14.6V9.4Z" />
              <path d="M13.242 10.7097C13.914 11.2567 14.25 11.5307 14.25 11.9997C14.25 12.4687 13.914 12.7427 13.242 13.2897C13.057 13.4417 12.872 13.5847 12.704 13.7027C12.5442 13.814 12.3807 13.92 12.214 14.0207C11.544 14.4277 11.208 14.6317 10.908 14.4057C10.608 14.1797 10.58 13.7087 10.525 12.7637C10.5097 12.5093 10.5014 12.2546 10.5 11.9997C10.5 11.7647 10.51 11.5027 10.525 11.2357C10.58 10.2907 10.607 9.81871 10.908 9.59371C11.208 9.36771 11.544 9.57171 12.214 9.97871C12.388 10.0847 12.555 10.1927 12.704 10.2967C12.873 10.4147 13.057 10.5577 13.242 10.7087" />
            </svg>
            Flythrough
          </button>
        </>
      )}

      {/* Optional tools (Add Object, Select Objects, Remove Object, Materials) were commented in Photorealistic — reintroduce via props/callbacks if needed. */}
      <div className="flex-1 min-h-2" aria-hidden="true" />
      <button
        type="button"
        onClick={onBackToDashboard}
        className="w-full flex flex-col items-center justify-center gap-2 text-[10px] font-medium text-gray-700 bg-white hover:bg-gray-100 border-gray-200 py-2 px-3 rounded-lg transition-colors shrink-0"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="shrink-0"
          aria-hidden="true"
        >
          <path
            d="M0.75 4.5C0.75 2.732 0.75 1.848 1.3 1.3C1.847 0.75 2.731 0.75 4.5 0.75C6.269 0.75 7.152 0.75 7.7 1.3C8.25 1.848 8.25 2.732 8.25 4.5C8.25 6.268 8.25 7.152 7.7 7.7C7.152 8.25 6.268 8.25 4.5 8.25C2.732 8.25 1.848 8.25 1.3 7.7C0.75 7.153 0.75 6.269 0.75 4.5ZM0.75 15.007C0.75 13.239 0.75 12.355 1.3 11.807C1.848 11.257 2.732 11.257 4.5 11.257C6.268 11.257 7.152 11.257 7.7 11.807C8.25 12.355 8.25 13.239 8.25 15.007C8.25 16.775 8.25 17.659 7.7 18.207C7.152 18.757 6.268 18.757 4.5 18.757C2.732 18.757 1.848 18.757 1.3 18.207C0.75 17.66 0.75 16.775 0.75 15.007ZM11.25 4.5C11.25 2.732 11.25 1.848 11.8 1.3C12.348 0.75 13.232 0.75 15 0.75C16.768 0.75 17.652 0.75 18.2 1.3C18.75 1.848 18.75 2.732 18.75 4.5C18.75 6.268 18.75 7.152 18.2 7.7C17.652 8.25 16.768 8.25 15 8.25C13.232 8.25 12.348 8.25 11.8 7.7C11.25 7.152 11.25 6.268 11.25 4.5ZM11.25 15.007C11.25 13.239 11.25 12.355 11.8 11.807C12.348 11.257 13.232 11.257 15 11.257C16.768 11.257 17.652 11.257 18.2 11.807C18.75 12.355 18.75 13.239 18.75 15.007C18.75 16.775 18.75 17.659 18.2 18.207C17.652 18.757 16.768 18.757 15 18.757C13.232 18.757 12.348 18.757 11.8 18.207C11.25 17.659 11.25 16.775 11.25 15.007Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Dashboard
      </button>
    </aside>
  );
}
